import type { AniTheme } from "./tokens";

export const XRAY_TOKENS = {
  "--ani-inspection-ink": "#dbf6ff",
  "--ani-inspection-accent": "#70edff",
  "--ani-inspection-title": "#ffffff",
  "--ani-inspection-title-family": "var(--font-neue-regrade-variable), var(--ani-family-heading)",
  "--ani-inspection-control-text": "#9bebfb",
  "--ani-inspection-control-border-light": "#dae2ed",
  "--ani-inspection-icon": "#91a0a9",
  "--ani-inspection-background": "#06131d",
  "--ani-inspection-surface": "#102c3f",
  "--ani-inspection-card-background": "#091724e6",
  "--ani-inspection-border": "#315165",
  "--ani-inspection-muted": "#95b5c6",
  "--ani-inspection-subtle": "#8aa6b8",
  "--ani-inspection-action-text": "#061722",
  "--ani-inspection-action-hover": "#164456",
  "--ani-inspection-action-pressed": "#1c5365",
  "--ani-inspection-control-background": "#102637",
  "--ani-inspection-control-border": "#37768a",
  "--ani-inspection-control-border-strong": "#88c5da",
  "--ani-inspection-tag-background": "#303030",
  "--ani-inspection-tag-text": "#eeeeee",
  "--ani-inspection-tag-hover": "#235b6e",
  "--ani-inspection-boundary": "#70edff70",
  "--ani-inspection-nested": "#e495ff70",
  "--ani-inspection-selected": "#ffffff",
  "--ani-inspection-glow": "0 0 16px #70edff80",
  "--ani-inspection-tooltip-border": "#70edff",
  "--ani-inspection-tooltip-background": "#06131df5",
  "--ani-inspection-tooltip-text": "#dbf6ff",
  "--ani-inspection-tooltip-shadow": "0 4px 16px #0006",
  "--ani-inspection-padding": "#e495ff40",
  "--ani-inspection-shadow": "0 0 24px #46d9ef0d",
};

/** Inspection ink is separate from the theme whose tokens/contrast are reported. */
export function xrayTheme(theme: AniTheme): AniTheme {
  return { ...theme, vars: { ...theme.vars, ...XRAY_TOKENS,
    "--ani-primary": "var(--ani-inspection-accent)",
    "--ani-neutral-00": "var(--ani-inspection-background)",
    "--ani-surface": "var(--ani-inspection-surface)",
    "--ani-text": "var(--ani-inspection-ink)",
    "--ani-text-secondary": "var(--ani-inspection-ink)",
    "--ani-text-muted": "var(--ani-inspection-muted)",
    "--ani-border-color": "var(--ani-inspection-border)",
    "--ani-shadow-sm": "var(--ani-inspection-shadow)",
    "--ani-button-primary-color": "var(--ani-inspection-accent)",
    "--ani-button-primary-color-hover": "#9BF3FF",
    "--ani-button-primary-color-pressed": "#4ACCDD",
    "--ani-button-primary-color-disabled": "var(--ani-inspection-control-background)",
    "--ani-button-primary-text-color-disabled": "var(--ani-inspection-muted)",
    "--ani-button-primary-text-color": "var(--ani-inspection-action-text)",
    "--ani-button-secondary-background-hover": "var(--ani-inspection-action-hover)",
    "--ani-button-secondary-background-pressed": "var(--ani-inspection-action-pressed)",
    "--ani-button-secondary-text-color-hover": "var(--ani-inspection-ink)",
    "--ani-button-tertiary-color-pressed": "var(--ani-inspection-accent)",
    "--ani-focus-outline-color": "var(--ani-inspection-accent)",
    "--ani-card-readonly-background": "var(--ani-inspection-card-background)",
    "--ani-card-readonly-border-color": "var(--ani-inspection-border)",
    "--ani-calendar-base-background": "var(--ani-inspection-background)",
    "--ani-calendar-base-border-color": "var(--ani-inspection-border)",
    "--ani-calendar-weekly-text-color": "var(--ani-inspection-ink)",
    "--ani-calendar-day-text-color": "var(--ani-inspection-ink)",
    "--ani-calendar-day-next-text-color": "var(--ani-inspection-subtle)",
    "--ani-calendar-day-today-text-color": "var(--ani-inspection-accent)",
    "--ani-combobox-background": "var(--ani-inspection-control-background)",
    "--ani-combobox-color": "var(--ani-inspection-border)",
    "--ani-combobox-text-color": "var(--ani-inspection-ink)",
    "--ani-combobox-background-hover": "var(--ani-inspection-action-hover)",
    "--ani-combobox-color-hover": "var(--ani-inspection-accent)",
    "--ani-combobox-color-focus": "var(--ani-inspection-accent)",
    "--ani-combobox-outline-color-focus": "var(--ani-inspection-accent)",
    "--ani-checkbox-background": "var(--ani-inspection-background)",
    "--ani-checkbox-background-selected": "var(--ani-inspection-accent)",
    "--ani-checkbox-border-color": "var(--ani-inspection-control-border-strong)",
    "--ani-tag-status-color-neutral": "var(--ani-inspection-tag-background)",
    "--ani-tag-status-text-color-neutral": "var(--ani-inspection-tag-text)",
    "--ani-tag-removable-color": "var(--ani-inspection-surface)",
    "--ani-tag-removable-border-color": "var(--ani-inspection-control-border)",
    "--ani-tag-removable-text-color": "var(--ani-inspection-ink)",
    "--ani-tag-removable-icon-color": "var(--ani-inspection-accent)",
    "--ani-tag-removable-color-hover": "var(--ani-inspection-tag-hover)",
    "--ani-tag-removable-outline-focus": "var(--ani-inspection-accent)",
    "--ani-chart-header-title": "var(--ani-inspection-ink)",
    "--ani-chart-grid": "var(--ani-inspection-border)",
    "--ani-chart-footer-caption": "var(--ani-inspection-ink)",
    "--ani-chart-categorical-01": "var(--ani-inspection-accent)",
    "--ani-search-background": "var(--ani-inspection-background)",
    "--ani-search-border-color": "var(--ani-inspection-border)",
    "--ani-search-leading-icon-color": "var(--ani-inspection-ink)",
    "--ani-search-placeholder-color": "var(--ani-inspection-muted)",
    "--ani-search-action-icon-color": "var(--ani-inspection-accent)",
    "--ani-search-processing-color": "var(--ani-inspection-accent)",
    "--ani-search-processing-background": "color-mix(in srgb, var(--ani-inspection-accent) 12%, var(--ani-inspection-background))",
    "--ani-search-processing-shimmer": "var(--ani-inspection-accent)",
    "--ani-icon-color": "var(--ani-inspection-ink)",
    "--ani-divider-color": "var(--ani-inspection-border)",
    "--ani-label-text-color": "var(--ani-inspection-ink)",
    "--ani-avatar-outline-color": "var(--ani-inspection-background)",
    "--ani-button-group-border-radius": "var(--ani-radius-default)",
  } };
}
